import Card from 'components/Card';
import ErrorMessage from 'components/ErrorMessage';
import Loading from 'components/Loading';
import ProposalOptionCard from 'components/Proposal/ProposalOptionCard';
import {
  useProposalQuery,
  RosterRequestDataNewGroupReservation,
  RosterRequestQuery,
  AmenityRequirement,
} from 'generated/graphql';
import { calculateHotelNights } from 'utilities/calculateHotelNights';
import { formatCents } from 'utilities/money';
import { popToast } from 'utilities/popToast';

import BookingConfirmationCard from './BookingConfirmationCard';
import MeetingSpaceRequirementsCard from './MeetingSpaceRequirementsCard';

export type ConfirmedBookingProps = {
  request: RosterRequestQuery['rosterRequest'];
  requestData: RosterRequestDataNewGroupReservation;
};

const ConfirmedBooking = ({ request, requestData }: ConfirmedBookingProps) => {
  const { data, loading, error } = useProposalQuery({
    variables: {
      id: request?.proposalId || '',
    },
    onError: (error) => {
      popToast(error.message, { type: 'error' });
    },
    skip: !request?.proposalId,
  });

  if (!request) {
    return null;
  }

  // Check if we have actual booking data
  const reservationsWithBookings =
    request.order?.reservations?.filter((res) => res.booking) || [];

  // Show actual booking confirmations if available
  if (reservationsWithBookings.length > 0) {
    // Calculate grand total for all bookings
    const grandTotal = reservationsWithBookings.reduce((total, reservation) => {
      if (!reservation.booking) return total;
      return total + reservation.booking.grandTotalAfterTax;
    }, 0);

    return (
      <>
        {/* Individual Booking Cards */}
        {reservationsWithBookings.map((reservation, index: number) => (
          <Card key={reservation.id} className="mb-6 overflow-visible">
            {reservationsWithBookings.length > 1 && (
              <h3 className="mb-4 font-oswald text-lg font-bold uppercase text-gray-600">
                Hotel {index + 1} - Booking Confirmation
              </h3>
            )}
            <BookingConfirmationCard reservation={reservation} />
          </Card>
        ))}

        {/* Grand Total Section for Multiple Hotels */}
        {reservationsWithBookings.length > 1 && (
          <Card className="mb-6">
            <div className="rounded-lg border border-gray-200 bg-gray-50 p-4 sm:p-6">
              <div className="flex items-center justify-between">
                <span className="text-base font-medium text-gray-900">
                  Total for all {reservationsWithBookings.length} hotels
                </span>
                <span className="text-xl font-bold tabular-nums text-gray-900">
                  {formatCents(grandTotal)}
                </span>
              </div>
            </div>
          </Card>
        )}
      </>
    );
  }

  // Fallback to proposal view if no bookings yet
  if (loading) {
    return <Loading />;
  }

  if (!request.proposalId) {
    return (
      <Card className="overflow-visible">
        <div className="py-8 text-center">
          <h2 className="mb-4 font-oswald text-2xl font-bold uppercase text-gray-600">
            Request Confirmed
          </h2>
          <p className="text-gray-600">
            Your request has been confirmed. Booking details will appear here
            once your reservations are finalized.
          </p>
        </div>
      </Card>
    );
  }

  if (error || !data?.proposal) {
    return <ErrorMessage errorMessage={error?.message || 'No Data'} />;
  }

  const selectedOption = data.proposal.proposalOptions.find(
    (option) => option.id === data.proposal.preferredProposalOptionId,
  );
  if (!selectedOption || !request.requestData) {
    return (
      <ErrorMessage errorMessage={'No data, an option should be present'} />
    );
  }

  const { checkInDate: fallbackCheckIn, checkOutDate: fallbackCheckOut } =
    requestData;
  const fallbackNumOfNights = calculateHotelNights(
    fallbackCheckIn,
    fallbackCheckOut,
  );

  // Initialize line item selections from the selected option
  const selectedLineItems: Record<string, boolean> = {};
  selectedOption.hotels.forEach((hotel) => {
    hotel.lineItems.forEach((item) => {
      selectedLineItems[item.id] = item.selected;
    });
  });

  // Get meeting space requirements from proposal data
  const proposalRequestData = data.proposal.requestData;
  const meetingSpace =
    proposalRequestData?.__typename === 'RosterRequestDataNewGroupReservation'
      ? proposalRequestData.meetingSpace
      : undefined;
  const meetingSpaceRequirements =
    proposalRequestData?.__typename === 'RosterRequestDataNewGroupReservation'
      ? proposalRequestData.meetingSpaceRequirements
      : undefined;

  return (
    <>
      <Card className="overflow-visible">
        <h2 className="font-oswald text-2xl font-bold uppercase text-secondaryBlue">
          Booked
        </h2>
        <div className="mt-6">
          <ProposalOptionCard
            option={selectedOption}
            optionIndex={0}
            totalNights={fallbackNumOfNights}
            isPreferred={true}
            onChooseOption={() => {}}
            showSelect={false}
            selectedLineItems={selectedLineItems}
            onLineItemToggle={() => {}}
            readOnly={true}
          />
        </div>
      </Card>

      {meetingSpace === AmenityRequirement.Required &&
        meetingSpaceRequirements && (
          <MeetingSpaceRequirementsCard
            meetingSpace={meetingSpace}
            meetingSpaceRequirements={meetingSpaceRequirements}
          />
        )}
    </>
  );
};

export default ConfirmedBooking;
